import React, { useState, useEffect } from 'react';
import { Dialog } from 'primereact/dialog';
import { Checkbox } from 'primereact/checkbox';
import { ColorMode, Orientation, PaperSize, PrintDisplayOptions } from './PrintService';
import { GlobalRadioButtons } from '../../../components/GlobalComponents/GlobalRadioButtons';
import { GlobalButton } from '../../../components/GlobalComponents/GlobalButton';

// ─────────────────────────────────────────────────────────────────────────────
// PrintOptionsModal — page setup + display toggles, triggers the actual print
// ─────────────────────────────────────────────────────────────────────────────

interface PrintOptionsModalProps {
  visible: boolean;
  onHide: () => void;
  title: string;
  subtitle?: string;
  onPrint: (opts: { orientation: Orientation; paperSize: PaperSize; displayOptions: PrintDisplayOptions }) => void;
}

const PrintOptionsModal: React.FC<PrintOptionsModalProps> = ({
  visible,
  onHide,
  title,
  subtitle,
  onPrint,
}) => {
  const [orientation, setOrientation] = useState<Orientation>('landscape');
  const [paperSize, setPaperSize] = useState<PaperSize>('A4');
  const [colorMode, setColorMode] = useState<ColorMode>('color');
  const [showPercentage, setShowPercentage] = useState(true);
  const [calculatePercentage, setCalculatePercentage] = useState(true);
  const [showGrade, setShowGrade] = useState(true);
  const [isClosing, setIsClosing] = useState(false);

  useEffect(() => {
    if (isClosing) {
      const dialog = document.querySelector('.printopts-modal-wrapper .p-dialog') as HTMLElement | null;
      const mask   = document.querySelector('.printopts-modal-wrapper')           as HTMLElement | null;
      if (dialog) dialog.classList.add('dialog-closing');
      if (mask)   mask.classList.add('mask-closing');
    }
  }, [isClosing]);

  const handleClose = () => {
    setIsClosing(true);
    setTimeout(() => { onHide(); setIsClosing(false); }, 380);
  };

  const handlePrint = () => {
    onPrint({ orientation, paperSize, displayOptions: { showPercentage, calculatePercentage, showGrade, colorMode } });
    handleClose();
  };

  const headerTemplate = () => (
    <div style={{ display: 'flex', alignItems: 'center', gap: 14, padding: '4px 0' }}>
      <div style={{
        width: 48, height: 48, borderRadius: 13,
        background: 'linear-gradient(135deg,#1e3a8a,#3b82f6)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        flexShrink: 0, boxShadow: '0 6px 18px rgba(59,130,246,.25)',
        border: '2.5px solid white',
      }}>
        <i className="pi pi-print" style={{ fontSize: 20, color: 'white' }} />
      </div>
      <div>
        <div style={{ fontSize: 18, fontWeight: 700, color: '#1e40af' }}>{title}</div>
        {subtitle && <div style={{ fontSize: 13, color: '#64748b', marginTop: 2 }}>{subtitle}</div>}
      </div>
    </div>
  );

  return (
    <Dialog
      visible={visible && !isClosing}
      onHide={handleClose}
      header={headerTemplate}
      style={{ width: '94vw', maxWidth: 460, borderRadius: 22, overflow: 'hidden' }}
      contentStyle={{
        padding: '18px 24px 8px',
        background: 'linear-gradient(180deg,#f8fafc,#f1f5f9)',
      }}
      headerStyle={{
        background: 'white',
        borderBottom: '1px solid #e5e7eb',
        padding: '18px 24px',
        borderRadius: '22px 22px 0 0',
      }}
      modal
      draggable={false}
      resizable={false}
      blockScroll
      dismissableMask
      maskClassName="printopts-modal-wrapper"
    >
      <style>{`
        .printopts-modal-wrapper { backdrop-filter:blur(4px); background-color:rgba(0,0,0,.22)!important; animation:poFadeIn .3s ease; }
        .printopts-modal-wrapper.mask-closing { animation:poFadeOut .38s ease!important; }
        @keyframes poFadeIn { from{opacity:0} to{opacity:1} }
        @keyframes poFadeOut { from{opacity:1} to{opacity:0} }
        .printopts-modal-wrapper .p-dialog { animation:poSlideIn .3s cubic-bezier(.4,0,.2,1); box-shadow:0 20px 48px -8px rgba(0,0,0,.22)!important; }
        .printopts-modal-wrapper .p-dialog.dialog-closing { animation:poSlideOut .38s cubic-bezier(.4,0,.2,1)!important; }
        @keyframes poSlideIn { from{opacity:0;transform:scale(.93) translateY(-16px)} to{opacity:1;transform:scale(1) translateY(0)} }
        @keyframes poSlideOut { from{opacity:1;transform:scale(1) translateY(0)} to{opacity:0;transform:scale(.9) translateY(18px)} }
      `}</style>

      <div style={{ marginBottom: 18 }}>
        <GlobalRadioButtons
          label="Orientation"
          options={[
            { value: 'portrait', label: 'Portrait' },
            { value: 'landscape', label: 'Landscape' },
          ]}
          value={orientation}
          onChange={(v) => setOrientation(v as Orientation)}
          layout="horizontal"
          gap={20}
        />
      </div>

      <div style={{ marginBottom: 18 }}>
        <GlobalRadioButtons
          label="Paper Size"
          options={[
            { value: 'A4', label: 'A4' },
            { value: 'A3', label: 'A3' },
          ]}
          value={paperSize}
          onChange={(v) => setPaperSize(v as PaperSize)}
          layout="horizontal"
          gap={20}
        />
      </div>

      <div style={{ marginBottom: 18 }}>
        <GlobalRadioButtons
          label="Print Type"
          options={[
            { value: 'color', label: 'Color' },
            { value: 'bw', label: 'Black & White' },
          ]}
          value={colorMode}
          onChange={(v) => setColorMode(v as ColorMode)}
          layout="horizontal"
          gap={20}
        />
      </div>

      <div style={{ marginBottom: 10 }}>
        <label style={{ display: 'block', fontSize: 12, fontWeight: 700, color: '#374151', marginBottom: 8 }}>
          Percentage Column
        </label>
        <div style={{ display: 'flex', gap: 10 }}>
          <div
            onClick={() => {
              setShowPercentage((v) => {
                const next = !v;
                // Turning the column off makes "calculate" moot; turning it
                // back on restores the sensible default of calculating.
                if (!next) setCalculatePercentage(true);
                return next;
              });
            }}
            style={{
              flex: 1, display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer',
              background: showPercentage ? '#EFF4FF' : '#F9FAFB',
              border: `1px solid ${showPercentage ? 'rgba(40,85,174,0.3)' : '#E5E7EB'}`,
              borderRadius: 10, padding: '9px 10px',
            }}
          >
            <i className="pi pi-eye" style={{ fontSize: 13, color: showPercentage ? '#2855AE' : '#9CA3AF' }} />
            <span style={{ fontSize: 12, fontWeight: 700, flex: 1, color: showPercentage ? '#111827' : '#6b7280' }}>Show %</span>
            <Checkbox checked={showPercentage} onChange={() => {
              setShowPercentage((v) => {
                const next = !v;
                if (!next) setCalculatePercentage(true);
                return next;
              });
            }} />
          </div>
          <div
            onClick={() => showPercentage && setCalculatePercentage((v) => !v)}
            style={{
              flex: 1, display: 'flex', alignItems: 'center', gap: 6,
              cursor: showPercentage ? 'pointer' : 'not-allowed',
              opacity: showPercentage ? 1 : 0.5,
              background: calculatePercentage ? '#EFF4FF' : '#F9FAFB',
              border: `1px solid ${calculatePercentage ? 'rgba(40,85,174,0.3)' : '#E5E7EB'}`,
              borderRadius: 10, padding: '9px 10px',
            }}
          >
            <i className="pi pi-calculator" style={{ fontSize: 13, color: calculatePercentage ? '#2855AE' : '#9CA3AF' }} />
            <span style={{ fontSize: 12, fontWeight: 700, flex: 1, color: calculatePercentage ? '#111827' : '#6b7280' }}>Calculate %</span>
            <Checkbox
              checked={calculatePercentage}
              disabled={!showPercentage}
              onChange={() => setCalculatePercentage((v) => !v)}
            />
          </div>
        </div>
        <div style={{ fontSize: 10.5, color: '#94A3B8', marginTop: 5, lineHeight: 1.4 }}>
          {showPercentage && !calculatePercentage
            ? 'Column will print blank ("—") for hand-filling.'
            : ''}
        </div>
      </div>

      <div style={{ marginBottom: 20 }}>
        <div
          onClick={() => setShowGrade((v) => !v)}
          style={{
            display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer',
            background: showGrade ? '#EFF4FF' : '#F9FAFB',
            border: `1px solid ${showGrade ? 'rgba(40,85,174,0.3)' : '#E5E7EB'}`,
            borderRadius: 10, padding: '9px 10px',
          }}
        >
          <i className="pi pi-star" style={{ fontSize: 13, color: showGrade ? '#2855AE' : '#9CA3AF' }} />
          <span style={{ fontSize: 12, fontWeight: 700, flex: 1, color: showGrade ? '#111827' : '#6b7280' }}>Show Grade</span>
          <Checkbox checked={showGrade} onChange={() => setShowGrade((v) => !v)} />
        </div>
      </div>

      <div style={{ marginBottom: 6 }}>
        <GlobalButton
          label="Print / Save PDF"
          icon="pi pi-print"
          fullWidth
          onClick={handlePrint}
        />
      </div>
    </Dialog>
  );
};

export default PrintOptionsModal;